import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { verifyResetToken, confirmPasswordResetWithToken } from './auth';
import './Auth.css';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token'); // YOUR custom token parameter

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validToken, setValidToken] = useState(false);
  const [tokenChecked, setTokenChecked] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  // Verify the custom token when component loads
  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        setError('Invalid reset link. Please request a new password reset.');
        setTokenChecked(true);
        return;
      }

      try {
        const verification = await verifyResetToken(token);
        
        if (verification.success) {
          setValidToken(true);
          setUserEmail(verification.email);
        } else {
          setError(verification.error);
        }
      } catch (error) {
        console.error('Error verifying token:', error);
        setError('Failed to verify reset link.');
      } finally {
        setTokenChecked(true);
      }
    };

    checkToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      const result = await confirmPasswordResetWithToken(token, newPassword);
      
      if (result.success) {
        setSuccess('Password reset successful! You can now log in with your new password.');
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(result.error);
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setError('Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Loading state while checking token
  if (!tokenChecked) {
    return (
      <div className="auth-container centered">
        <div className="auth-card">
          <div className="auth-header">
            <h1>A R T I F E X</h1>
            <h2>Verifying Reset Link...</h2>
          </div>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!validToken) {
    return (
      <div className="auth-container centered">
        <div className="auth-card">
          <div className="auth-header">
            <h1>A R T I F E X</h1>
            <h2>Invalid Reset Link</h2>
          </div>
          <div className="auth-form">
            <div className="error-message">
              {error}
            </div>
            <p>This password reset link is invalid or has expired. Please request a new password reset.</p>
            <button 
              onClick={() => navigate('/forgot-password')}
              className="auth-button primary"
            >
              Request New Reset
            </button>
            <button 
              onClick={() => navigate('/login')}
              className="auth-button secondary"
              style={{ marginTop: '10px' }}
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container centered">
      <div className="auth-card">
        <div className="auth-header">
          <h1>A R T I F E X</h1>
          <h2>Reset Your Password</h2>
          <p>Reset password for {userEmail}</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {success && (
            <div className="success-message">
              {success}
              <p style={{ marginTop: '10px', fontSize: '14px' }}>
                Redirecting to login in 3 seconds...
              </p>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password (min 6 characters)"
              required
              disabled={loading}
              minLength={6}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              disabled={loading}
              minLength={6}
            />
          </div>

          <button 
            type="submit" 
            className="auth-button primary"
            disabled={loading || success}
          >
            {loading ? 'Resetting Password...' : 'Reset Password'}
          </button>

          <div className="auth-links">
            <button 
              type="button"
              onClick={() => navigate('/login')}
              className="link-button"
              disabled={loading}
            >
              Back to Login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;